/**
 * Moderation Work Queue Page
 *
 * Displays assigned moderation cases for review and allows moderators to take actions.
 */

import { useCurrentCommunityId } from '@communities/community/hooks';
import { HeroBackground } from '@core/components/HeroBackground';
import { track } from '@core/logging';
import { useAssignModerationCase, useSubmitCaseFeedback } from '@moderation/hooks';
import { Badge, BadgeVariant, Button, ButtonType, Heading, Text, Icon, IconSize, Completed, Warning, Close } from '@soteria/design-system-ve';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as styles from './ModerationWorkQueuePage.css';

interface ModerationCase {
  id: string;
  caseId: string;
  topicName: string;
  report: {
    communityId: string;
    topicName: string;
    subjectId: string;
    timestamp: string;
    contents: Array<{
      contentId: string;
      contentType: string;
      data: string;
      labels: Array<{
        labels: Array<{
          label: string;
          confidence: number;
        }>;
        source: string | null;
      }> | null;
    }> | null;
  };
  status: string;
  createdAt: string;
}

function EmptyQueueState({ onReturn }: { onReturn: () => void }): ReactElement {
  return (
    <div>
      <HeroBackground />
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <Icon icon={Completed} iconSize={IconSize.XLarge} />
          </div>
          <Heading level="h2">No Cases Available</Heading>
          <Text>There are no moderation cases available for this topic at the moment.</Text>
          <Button buttonType={ButtonType.Primary} onClick={onReturn}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

function CompletedState({ totalCases, onReturn }: { totalCases: number; onReturn: () => void }): ReactElement {
  return (
    <div>
      <HeroBackground />
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <Icon icon={Completed} iconSize={IconSize.XLarge} />
          </div>
          <Heading level="h2">All Cases Completed!</Heading>
          <Text>
            You've reviewed all {totalCases} case{totalCases !== 1 ? 's' : ''} in this batch.
          </Text>
          <Button buttonType={ButtonType.Primary} onClick={onReturn}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

function LoadingState(): ReactElement {
  return (
    <div>
      <HeroBackground />
      <div className={styles.container}>
        <Text>Loading cases...</Text>
      </div>
    </div>
  );
}

/* eslint-disable max-statements, complexity */
export function ModerationWorkQueuePage() {
  const navigate = useNavigate();
  const { topicId } = useParams<{ topicId: string }>();
  const communityId = useCurrentCommunityId();

  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const [localCases, setLocalCases] = useState<ModerationCase[]>([]);

  const { assignCases, cases, loading: assignLoading } = useAssignModerationCase();
  const { submitFeedback, loading: submitLoading } = useSubmitCaseFeedback();

  // Convert topic ID from URL format to display format
  const topicName = topicId
    ? topicId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  // Auto-assign cases when page loads
  useEffect(() => {
    if (communityId && topicName && localCases.length === 0) {
      void assignCases({
        variables: {
          communityId,
          topicName,
        },
      });
    }
  }, [communityId, topicName, assignCases, localCases.length]);

  // Update local cases when assignment completes
  useEffect(() => {
    if (cases.length > 0) {
      setLocalCases(cases);
    }
  }, [cases]);

  const currentCase = localCases[currentCaseIndex];
  const totalCases = localCases.length;
  const hasMoreCases = currentCaseIndex < totalCases - 1;

  // Extract content and labels from the case
  const firstContent = currentCase?.report?.contents?.[0];
  const contentText = firstContent?.data || '(No content available)';
  const allLabels =
    firstContent?.labels?.flatMap(labelGroup =>
      labelGroup.labels.map(l => ({
        name: l.label,
        confidence: l.confidence,
        severity: 'MEDIUM', // Default severity
      }))
    ) || [];

  const handleAction = async (decision: 'APPROVE' | 'REJECT' | 'ESCALATE') => {
    if (!currentCase) return;

    try {
      await submitFeedback({
        caseId: currentCase.caseId,
        decision,
      });

      // Move to next case or finish
      if (hasMoreCases) {
        setCurrentCaseIndex(prev => prev + 1);
      } else {
        // All cases completed
        navigate(`/communities/${communityId}/moderation`);
      }
    } catch (error) {
      track('moderation_feedback_submit_failed', {
        caseId: currentCase?.caseId,
        decision,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const handleSkip = () => {
    if (hasMoreCases) {
      setCurrentCaseIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentCaseIndex > 0) {
      setCurrentCaseIndex(prev => prev - 1);
    }
  };

  const handleReturnToDashboard = () => {
    navigate(`/communities/${communityId}/moderation`);
  };

  if (assignLoading) {
    return <LoadingState />;
  }

  if (!currentCase && totalCases === 0) {
    return <EmptyQueueState onReturn={handleReturnToDashboard} />;
  }

  if (!currentCase) {
    return <CompletedState totalCases={totalCases} onReturn={handleReturnToDashboard} />;
  }

  return (
    <div>
      <HeroBackground />
      <div className={styles.container}>
        {/* Header with Progress */}
        <div className={styles.header}>
          <div>
            <Heading level="h1">{topicName} Moderation Queue</Heading>
            <Text className={styles.progressText}>
              Case {currentCaseIndex + 1} of {totalCases}
            </Text>
          </div>
          <Button buttonType={ButtonType.Secondary} onClick={handleReturnToDashboard}>
            Exit Queue
          </Button>
        </div>

        {/* Case Information */}
        <div className={styles.caseCard}>
          <div className={styles.caseHeader}>
            <div>
              <Heading level="h2">Case Review</Heading>
              <Text>
                <strong>Content ID:</strong> {firstContent?.contentId || 'N/A'}
              </Text>
              <Text>
                <strong>Status:</strong> {currentCase.status}
              </Text>
            </div>
            <Badge variant={BadgeVariant.Default}>{currentCase.topicName}</Badge>
          </div>

          {/* Content Section */}
          <div className={styles.contentSection}>
            <Heading level="h3">Content</Heading>
            <div className={styles.contentBox}>{contentText}</div>
          </div>

          {/* Labels Section */}
          <div className={styles.labelsSection}>
            <Heading level="h3">Detected Labels</Heading>
            <div className={styles.labelsList}>
              {allLabels.length > 0 ? (
                allLabels.map(label => (
                  <div key={`${label.name}-${label.confidence}`} className={styles.labelItem}>
                    <div className={styles.labelInfo}>
                      <Badge
                        variant={
                          label.severity === 'HIGH' || label.severity === 'CRITICAL'
                            ? BadgeVariant.Error
                            : label.severity === 'MEDIUM'
                              ? BadgeVariant.Warning
                              : BadgeVariant.Default
                        }
                      >
                        {label.name}
                      </Badge>
                      <Text className={styles.confidenceText}>{Math.round(label.confidence * 100)}%</Text>
                    </div>
                    <Text className={styles.confidenceText}>Severity: {label.severity}</Text>
                  </div>
                ))
              ) : (
                <Text>No labels detected</Text>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className={styles.actionBar}>
          <div className={styles.navigationButtons}>
            <Button buttonType={ButtonType.Secondary} disabled={currentCaseIndex === 0} onClick={handlePrevious}>
              ← Previous
            </Button>
            <Button buttonType={ButtonType.Secondary} disabled={!hasMoreCases} onClick={handleSkip}>
              Skip
            </Button>
          </div>

          <div className={styles.actionButtons}>
            <Button buttonType={ButtonType.Primary} disabled={submitLoading} onClick={() => handleAction('APPROVE')}>
              <Icon icon={Completed} iconSize={IconSize.Small} /> Approve
            </Button>
            <Button buttonType={ButtonType.Secondary} disabled={submitLoading} onClick={() => handleAction('ESCALATE')}>
              <Icon icon={Warning} iconSize={IconSize.Small} /> Escalate
            </Button>
            <Button buttonType={ButtonType.Danger} disabled={submitLoading} onClick={() => handleAction('REJECT')}>
              <Icon icon={Close} iconSize={IconSize.Small} /> Reject
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
